import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {OpportunitySourceOption} from '@hisense/shared';
import {apiFetch,getLocalizedError} from '@/lib/api';
import {SearchableFilter,type SearchableFilterOption} from '@/components/ui/SearchableFilter';
/**
 * Chọn khách hàng / PI cho cơ hội bán hàng — MỘT ô duy nhất.
 *
 * Trước đây có thêm một ô tìm kiếm riêng nằm trên combobox, trong khi bản thân
 * combobox đã có ô tìm kiếm bên trong: hai ô cho cùng một việc, người dùng
 * không đoán được ô nào lọc cái gì. Nay ô tìm kiếm sẵn có bên trong gọi thẳng
 * máy chủ (danh mục khách lớn hơn một lần tải).
 */
export function OpportunitySourcePicker({kind,customerId='',value,label,onChange}:{kind:'customers'|'pis';customerId?:string;value:string;label?:string;onChange:(id:string)=>void}){
 const {t}=useTranslation(),[q,setQ]=useState('');
 /**
  * Nhớ dòng vừa chọn. Sau khi đóng danh sách, chuỗi tìm kiếm được xoá và danh
  * sách tải lại — dòng đã chọn có thể không còn trong trang kết quả mới, và ô
  * sẽ hiện ra id thô thay vì tên khách.
  */
 const [picked,setPicked]=useState<SearchableFilterOption|null>(null);
 const query=useQuery({queryKey:['opportunity-options',kind,customerId,q],queryFn:({signal})=>apiFetch<{data:OpportunitySourceOption[];hasMore:boolean}>(`/sales-opportunities/${kind}?q=${encodeURIComponent(q)}&customerId=${encodeURIComponent(customerId)}`,{signal}),enabled:kind==='customers'||!!customerId});
 const rows:SearchableFilterOption[]=(query.data?.data??[]).map(r=>({value:r.id,label:r.label,sublabel:r.detail}));
 const known=rows.some(o=>o.value===value);
 const fallback=!value||known?null:picked?.value===value?picked:label?{value,label}:null;
 const options=fallback?[fallback,...rows]:rows;
 const text=t(kind==='customers'?'crm.findCustomer':'crm.findPi');
 return <SearchableFilter
  className="w-full"
  label={t(kind==='customers'?'crm.customer':'crm.pi')}
  value={value} allLabel={t('warehouse.select')} includeAll={false}
  options={options}
  searchPlaceholder={text} searchValue={q} onSearchChange={setQ}
  onChange={id=>{setPicked(rows.find(o=>o.value===id)??null);onChange(id);}}
  isLoading={query.isLoading} error={query.error?getLocalizedError(query.error,t,'crm.error'):null} onRetry={()=>void query.refetch()}
  footer={query.data?.hasMore?()=><p className="px-2 py-1 text-2xs text-ink-soft">{t('crm.refine')}</p>:undefined}
 />;
}
